import React, { useMemo, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Paper, Stack } from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import { CommonButton } from '@/components/common'
import { isEmpty } from '@/utils'
import { ROUTES } from '@/constants'
import { usePortalConstantValue } from '@/hooks'
import { useTenantContext } from '@/context/TenantContext'

export const LoginStatus = ({ id = 'common-login-status', donor, style }) => {
  const { pathname } = useLocation()
  const isLoggedIn = donor && !isEmpty(donor)
  const getPortalConstantValue = usePortalConstantValue()
  const { tenant } = useTenantContext()

  // Check if Loyalty Store should be enabled
  const isLoyaltyStoreEnabled = useMemo(() => {
    if (!isLoggedIn || !donor?.dsid) return false

    return !isEmpty(tenant?.loyalty_store_url)
  }, [isLoggedIn, donor?.dsid, tenant])

  // Handle Loyalty Store click - opens in new tab
  const handleLoyaltyStoreClick = useCallback(() => {
    if (!donor?.id) return

    // Open loyalty-auth in new tab - it will call the endpoint and handle redirects
    window.open(ROUTES.DONOR_LOYALTY_AUTH)
  }, [donor?.id])

  const actions = useMemo(
    () =>
      isLoggedIn
        ? [
            {
              label: 'My Appointments',
              to: `${ROUTES.DONOR_APPOINTMENTS}`,
            },
            {
              label: 'Health History',
              to: `${ROUTES.DONOR_HEALTH_HISTORY}`,
            },
            ...(isLoyaltyStoreEnabled
              ? [
                  {
                    label: 'Loyalty Store',
                    to: `${ROUTES.DONOR_LOYALTY_AUTH}`,
                    startIcon: <StorefrontIcon />,
                  },
                ]
              : []),
          ]
        : [
            {
              label: getPortalConstantValue('LOGIN_BUTTON_TEXT'),
              disabled: pathname === ROUTES.LOGIN,
              to: `${ROUTES.LOGIN}`,
            },
            // Not needed for now, login and account recovery are the only auth routes
            // {
            //   label: 'Register',
            //   disabled: pathname === ROUTES.REGISTER,
            //   to: `${ROUTES.REGISTER}`,
            // },
          ],
    [
      isLoggedIn,
      pathname,
      isLoyaltyStoreEnabled,
      handleLoyaltyStoreClick,
      getPortalConstantValue,
    ]
  )

  return (
    <Paper
      id={id}
      data-testid={id}
      elevation={0}
      sx={{
        p: 2,
        width: '100%',
        height: isLoggedIn ? (isLoyaltyStoreEnabled ? 170 : 125) : 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'secondary.main',
        ...style,
      }}
    >
      <Stack spacing={1.5} sx={{ width: '100%' }}>
        {actions.map((a, index) => (
          <CommonButton
            key={`${id}-btn-${index}`}
            id={`${id}-btn-${index}`}
            label={a.label}
            disabled={a.disabled}
            to={a.isAction ? null : a.disabled ? null : a.to}
            onClick={a.isAction ? a.onClick : undefined}
            startIcon={a.startIcon}
            size='large'
            fullWidth
            sx={{ borderRadius: '10px', height: 44 }}
          />
        ))}
      </Stack>
    </Paper>
  )
}
